/**
 * Activity feed scoring constants shared by the API and the UI.
 *
 * A Space feed ranks with that Space's weights; feeds that span Spaces
 * (personal, app-wide) rank with {@link PLATFORM_ACTIVITY_WEIGHTS} so Space
 * admins cannot raise their reach outside their own Space.
 */
export const SPACE_ACTIVITY_WEIGHT_KEYS = [
    'reaction',
    'inlineReply',
    'threadReply',
    'ephemeralReply',
    'pinBonus',
    'announcement',
];
export const DEFAULT_SPACE_ACTIVITY_WEIGHTS = Object.freeze({
    reaction: 1,
    inlineReply: 2,
    threadReply: 3,
    ephemeralReply: 1,
    pinBonus: 10,
    announcement: 25,
});
export const PLATFORM_ACTIVITY_WEIGHTS = DEFAULT_SPACE_ACTIVITY_WEIGHTS;
/** Largest weight a Space admin may set. */
export const SPACE_ACTIVITY_WEIGHT_MAX = 100;
/** Minimum score for a message to appear in a feed. */
export const ACTIVITY_MIN_SCORE = 2;
/** Hot rank: every 10x in score is worth this much freshness (12.5 hours). */
export const ACTIVITY_HOT_DECAY_SEC = 45_000;
export function spaceActivityMessageScore(counts, pinned, weights) {
    return (weights.reaction * counts.reactors +
        weights.inlineReply * counts.replies +
        weights.threadReply * counts.threadReplies +
        weights.ephemeralReply * counts.ephemeralReplies +
        (pinned ? weights.pinBonus : 0));
}
export function isSpaceActivityWeight(value) {
    return Number.isInteger(value) && value >= 0 && value <= SPACE_ACTIVITY_WEIGHT_MAX;
}
/** Stored weights with anything missing or out of range replaced by the default. */
export function normalizeSpaceActivityWeights(stored) {
    const out = { ...DEFAULT_SPACE_ACTIVITY_WEIGHTS };
    for (const key of SPACE_ACTIVITY_WEIGHT_KEYS) {
        const value = stored?.[key];
        if (isSpaceActivityWeight(value))
            out[key] = value;
    }
    return out;
}
export function spaceActivityWeightsEqual(a, b) {
    return SPACE_ACTIVITY_WEIGHT_KEYS.every((key) => a[key] === b[key]);
}
export const SPACE_ACTIVITY_SORTS = ['new', 'hot', 'active', 'top'];
export const SPACE_ACTIVITY_TOP_WINDOWS = ['24h', '7d', '30d', 'all'];
/** Optional item kinds a viewer can toggle. Root messages are always included. */
export const SPACE_ACTIVITY_INCLUDES = ['announcements', 'pins', 'posts'];
export const SPACE_ACTIVITY_DEFAULT_SORT = 'hot';
export const SPACE_ACTIVITY_DEFAULT_TOP_WINDOW = '7d';
export const SPACE_ACTIVITY_PAGE_DEFAULT = 20;
export const SPACE_ACTIVITY_PAGE_MAX = 50;
/** Top with a time window ranks at most this many items. */
export const SPACE_ACTIVITY_TOP_WINDOW_MAX_ITEMS = 500;
/** Stored preferences with unknown values dropped; undefined when nothing valid remains. */
export function normalizeSpaceActivityPreferences(stored) {
    if (typeof stored !== 'object' || stored === null)
        return undefined;
    const raw = stored;
    const out = {};
    if (SPACE_ACTIVITY_SORTS.includes(raw.sort))
        out.sort = raw.sort;
    if (SPACE_ACTIVITY_TOP_WINDOWS.includes(raw.topWindow)) {
        out.topWindow = raw.topWindow;
    }
    if (typeof raw.include === 'object' && raw.include !== null) {
        const include = {};
        for (const key of SPACE_ACTIVITY_INCLUDES) {
            const value = raw.include[key];
            if (typeof value === 'boolean')
                include[key] = value;
        }
        if (Object.keys(include).length > 0)
            out.include = include;
    }
    return Object.keys(out).length > 0 ? out : undefined;
}
/** Weights stay locked this long after a rescore finishes. */
export const SPACE_ACTIVITY_RESCORE_COOLDOWN_MS = 15 * 60 * 1000;
/** The window weight changes are counted over. */
export const SPACE_ACTIVITY_WEIGHT_CHANGE_WINDOW_MS = 24 * 60 * 60 * 1000;
export const SPACE_ACTIVITY_WEIGHT_CHANGES_PER_DAY = 3;
/** Spaces with more members than this get {@link SPACE_ACTIVITY_LARGE_SPACE_WEIGHT_CHANGES_PER_DAY}. */
export const SPACE_ACTIVITY_LARGE_SPACE_MEMBERS = 1000;
export const SPACE_ACTIVITY_LARGE_SPACE_WEIGHT_CHANGES_PER_DAY = 1;
export function spaceActivityWeightChangesPerDay(memberCount) {
    return memberCount > SPACE_ACTIVITY_LARGE_SPACE_MEMBERS
        ? SPACE_ACTIVITY_LARGE_SPACE_WEIGHT_CHANGES_PER_DAY
        : SPACE_ACTIVITY_WEIGHT_CHANGES_PER_DAY;
}
export const DEFAULT_SPACE_ACTIVITY_SETTINGS = Object.freeze({
    defaultSort: SPACE_ACTIVITY_DEFAULT_SORT,
    defaultTopWindow: SPACE_ACTIVITY_DEFAULT_TOP_WINDOW,
    weights: DEFAULT_SPACE_ACTIVITY_WEIGHTS,
});
//# sourceMappingURL=space-activity.js.map